import type { PersonQuery, PersonRead } from '@pedigreesmart/api-client'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import './people.css'

const PER_PAGINA = 25

/**
 * L'anagrafica: soci, allevatori, proprietari.
 *
 * **La ricerca è per cognome, e c'è scritto.** Le persone, a differenza dei
 * soggetti, non hanno una ricerca libera sul backend: `?q=` verrebbe ignorato
 * in silenzio e l'elenco resterebbe intero, dando l'impressione che la ricerca
 * funzioni e non trovi corrispondenze. Meglio una casella che dice cosa cerca.
 * Il confronto è parziale e non distingue maiuscole: «bian» trova «Bianchi».
 *
 * **Il filtro «solo allevatori» è un interruttore e non una voce di un
 * menu a tendina**: in segreteria è la distinzione che si usa continuamente,
 * e nasconderla dentro un elenco di opzioni la rende un gesto in due tempi.
 */
export function PersonList({
  onApri,
  onNuova,
}: {
  onApri?: ((id: number) => void) | undefined
  onNuova?: (() => void) | undefined
}) {
  const { resources } = useSession()
  const [cognome, setCognome] = useState('')
  const [soloAllevatori, setSoloAllevatori] = useState(false)
  const [pagina, setPagina] = useState(1)

  const query = useQuery({
    queryKey: ['people', cognome, soloAllevatori, pagina],
    queryFn: ({ signal }) => {
      const filtri: PersonQuery = {
        page: pagina,
        items_per_page: PER_PAGINA,
        'order[last_name]': 'asc',
      }

      if (cognome.trim()) filtri.last_name = cognome.trim()
      // Solo quando è acceso: `breeder=false` è un filtro vero e nasconderebbe
      // gli allevatori, che non è quello che chiede un interruttore spento.
      if (soloAllevatori) filtri.breeder = true

      return resources.people.list(filtri, signal)
    },
    placeholderData: keepPreviousData,
  })

  const pagine = query.data ? Math.max(1, Math.ceil(query.data.totalItems / PER_PAGINA)) : 1

  return (
    <section aria-labelledby="titolo-persone">
      <div className="elenco__testa">
        <h1 id="titolo-persone">Persone</h1>
        <div className="elenco__azioni">
          {query.data && (
            <p className="elenco__conteggio" aria-live="polite">
              {query.data.totalItems === 1
                ? '1 persona'
                : `${query.data.totalItems.toLocaleString('it-IT')} persone`}
            </p>
          )}
          {onNuova && (
            <button type="button" onClick={onNuova}>
              Nuova persona
            </button>
          )}
        </div>
      </div>

      <div className="persone__filtri">
        <label className="elenco__ricerca">
          <span className="solo-lettore">Cerca per cognome o ragione sociale</span>
          <input
            type="search"
            value={cognome}
            placeholder="Cognome o ragione sociale"
            onChange={(event) => {
              setCognome(event.target.value)
              setPagina(1)
            }}
          />
        </label>

        <label className="persone__interruttore">
          <input
            type="checkbox"
            checked={soloAllevatori}
            onChange={(event) => {
              setSoloAllevatori(event.target.checked)
              setPagina(1)
            }}
          />
          Solo allevatori
        </label>
      </div>

      {query.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere l'anagrafica. {descriviErrore(query.error)}
        </p>
      )}

      {query.isPending && <p className="messaggio">Caricamento…</p>}

      {query.data && query.data.member.length === 0 && (
        <p className="messaggio">
          {cognome.trim() ? `Nessun risultato per «${cognome.trim()}».` : 'Nessuna persona in anagrafica.'}
        </p>
      )}

      {query.data && query.data.member.length > 0 && (
        <div className="tabella-scorrevole" data-attesa={query.isFetching ? 'si' : undefined}>
          <table>
            <thead>
              <tr>
                <th scope="col">Nome</th>
                <th scope="col">Affisso</th>
                <th scope="col">Città</th>
                <th scope="col">Contatto</th>
                <th scope="col">Ruolo</th>
              </tr>
            </thead>
            <tbody>
              {query.data.member.map((persona) => (
                <Riga key={persona.id} persona={persona} onApri={onApri} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagine > 1 && (
        <nav className="paginazione" aria-label="Pagine">
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina <= 1}
            onClick={() => {
              setPagina((p) => p - 1)
            }}
          >
            Precedente
          </button>
          <span aria-live="polite">
            Pagina {pagina} di {pagine}
          </span>
          <button
            type="button"
            data-variante="secondario"
            disabled={pagina >= pagine}
            onClick={() => {
              setPagina((p) => p + 1)
            }}
          >
            Successiva
          </button>
        </nav>
      )}
    </section>
  )
}

function Riga({
  persona,
  onApri,
}: {
  persona: PersonRead
  onApri?: ((id: number) => void) | undefined
}) {
  const apribile = typeof onApri === 'function'

  return (
    <tr
      className={apribile ? 'riga--apribile' : undefined}
      onClick={
        apribile
          ? () => {
              onApri(persona.id)
            }
          : undefined
      }
    >
      <td>
        {/* `display_name` viene dal server: comporlo qui vorrebbe dire
            riscrivere la regola che distingue persona fisica e giuridica. */}
        <span className="riga__nome">{persona.display_name}</span>
        {persona.anonymized_at && (
          <span className="riga__secondario">Dati cancellati su richiesta</span>
        )}
      </td>
      <td>{persona.kennel_prefix ?? '—'}</td>
      <td>{persona.city ?? '—'}</td>
      <td>{persona.email ?? persona.mobile ?? persona.phone ?? '—'}</td>
      <td>{persona.breeder ? 'Allevatore' : '—'}</td>
    </tr>
  )
}

function descriviErrore(errore: unknown): string {
  const stato = (errore as { status?: number } | null)?.status

  if (stato === 403) return 'Il vostro profilo non ha accesso all\'anagrafica.'
  if (stato === 401) return 'La sessione è scaduta: rientrare.'

  return 'Riprovare fra qualche istante.'
}
